import * as React from 'react'
import {
  ErrorBoundary as BaseErrorBoundary,
  FallbackProps
} from 'react-error-boundary'
import {useDispatch} from 'react-redux'
import {
  Source,
  trackSystemPageErrored
} from 'middleware/analytics/actions/trackSystemPageErrored'
import handleFatalError from 'modules/common/actions/errors'

import {debounce} from '@vio/core/src/utils/debounce'
import {ErrorBoundaryFallback} from '@vio/shared/src/search/ErrorBoundaryFallback'

import {DELAY_TRACK_ERROR_EVENT_TIME} from './constants'

interface Props {
  children?: React.ReactNode
}

const ErrorBoundaryFallbackWithOnClick: React.FC<FallbackProps> = props => {
  const navigateToHomepage = React.useCallback(() => {
    window.location.href = '/'
  }, [])
  return <ErrorBoundaryFallback onClick={navigateToHomepage} {...props} />
}

export const ErrorBoundary: React.FC<Props> = ({children}) => {
  const dispatch = useDispatch()

  const handleTrackSystemPageErrored = React.useCallback(
    (message: string) => {
      dispatch(
        trackSystemPageErrored({
          payload: {
            source: Source.AppErrorBoundary,
            message
          }
        })
      )
    },
    [dispatch]
  )

  const handleFatalErrorAction = (error: Error, errorInfo: React.ErrorInfo) => {
    dispatch(
      handleFatalError({
        error: error.message,
        errorInfo: errorInfo.componentStack
      })
    )
  }

  const debouncedTrackError = React.useMemo(
    () => debounce(handleTrackSystemPageErrored, DELAY_TRACK_ERROR_EVENT_TIME),
    [handleTrackSystemPageErrored]
  )

  const errorHandler = (error: Error, errorInfo: React.ErrorInfo) => {
    handleFatalErrorAction(error, errorInfo)
    debouncedTrackError(error.message)
  }

  return (
    <BaseErrorBoundary
      FallbackComponent={ErrorBoundaryFallbackWithOnClick}
      onError={errorHandler}
    >
      {children}
    </BaseErrorBoundary>
  )
}
